import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * The inside of a chart tooltip: a centered header, one dot·label / value row
 * per series, and an optional bold total.
 *
 * Extracted from `<ChartTooltip>` so a canvas chart can render the same
 * visual. `<ChartTooltip>` is an uncontrolled Radix tooltip driven by pointer
 * events on a DOM trigger; a canvas has no per-series elements to hang those
 * on, and its position comes from a crosshair callback rather than Popper. So
 * the canvas path renders this body inside its own absolutely-positioned
 * container instead.
 *
 * Both entry points share this one body deliberately — a third hand-rolled
 * tooltip layout is exactly what the app-wide rule exists to prevent.
 */
export interface ChartTooltipRow {
  /** Series label (left). */
  label: string;
  /** Pre-formatted value (right). Format via `@/lib/format` before passing. */
  value: string;
  /**
   * Swatch color — a CSS color or `var(--token)`. Match the series' color on
   * the chart. Omit for a row with no swatch.
   */
  dot?: string;
}

/**
 * Frame classes for a floating chart tooltip.
 *
 * Mirrors what `TooltipContent` applies, for the canvas overlay which sits
 * outside Radix and so cannot inherit them.
 */
export const CHART_TOOLTIP_FRAME_CLASS =
  "min-w-[196px] rounded-lg border border-border bg-card text-xs text-foreground shadow-raised";

export function ChartTooltipBody({
  title,
  rows,
  total,
}: {
  title?: React.ReactNode;
  rows: ChartTooltipRow[];
  total?: { label: string; value: string };
}) {
  return (
    <>
      {title != null ? (
        <div className="border-b border-border px-3 py-2 text-center text-xs font-semibold text-foreground">
          {title}
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5 px-3 py-2.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-4 text-sm">
            <span className="flex items-center gap-2 text-muted-foreground">
              {r.dot ? (
                <span
                  className={cn("h-2.5 w-2.5 shrink-0 rounded-full")}
                  style={{ background: r.dot }}
                  aria-hidden
                />
              ) : null}
              {r.label}
            </span>
            <span className="font-medium tabular-nums text-foreground">{r.value}</span>
          </div>
        ))}
        {total ? (
          <div className="mt-1 flex items-center justify-between gap-4 border-t border-border pt-1.5 text-sm font-semibold text-foreground">
            <span>{total.label}</span>
            <span className="tabular-nums">{total.value}</span>
          </div>
        ) : null}
      </div>
    </>
  );
}
